import type { IndustryPage } from "@/lib/data";

export const plumbers: IndustryPage = {
  slug: "plumbers",
  seoTitle:
    "Web Design for Plumbers UK | Fixed £1,000, Live in 7 Days",
  seoDescription:
    "Web design for plumbers: emergency callout pages, one-tap calls, quote forms and Gas Safe badges you hold. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for plumbers",
  tldr:
    "ConstructionWebDesign.uk offers web design for plumbers across the UK: mobile-first plumber websites with emergency callout pages, one-tap calling and the Gas Safe or WaterSafe details you hold. Each site costs £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. Your website is live in 7 days, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Plumber websites · UK",
    headline: "Website design for plumbers, built around emergency callouts.",
    highlight: "emergency callouts",
    sub: "Websites for UK plumbers with an emergency-led homepage, one-tap calling, a page for every service, a quick quote form, the Gas Safe, CIPHE or WaterSafe badges you hold and on-page local SEO.",
    secondaryCta: { label: "See plumber demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/plumbing_category.jpg",
      alt: "Plumber leaning into a copper pipe repair mid-motion, tools and fittings scattered around",
      headlineTop: "Build your plumbing",
      headlineBottom: "business, fast.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/brassline.jpg",
    },
  ],
  demoCategory: "Plumbers",
  demosHeading: "Plumber website demo templates.",
  demosSub:
    "Demo templates for plumbing and heating firms. Open one to see the layout, pages and features a plumber website can include. These are demos, not client sites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a plumber need a website?",
    intro:
      "A plumber needs a website because it gives a homeowner one place to see your services, the areas you cover, your reviews and your accreditations, then ring you in one tap. A plumber website keeps taking enquiries while you're on a job.",
    points: [
      {
        title: "Look like a proper business from the first visit.",
        body: "A clear, mobile-first plumber website with your services, the areas you cover and photos of your own jobs shows a homeowner you're established before they pick up the phone.",
        icon: "Shield",
      },
      {
        title: "Turn visits into calls, not clicks.",
        body: "Click-to-call buttons, a sticky number bar on mobile and a short enquiry form give every visitor an easy way to reach you, on every page of the site.",
        icon: "Phone",
      },
      {
        title: "Get found for \"plumber near me\".",
        body: "On-page local SEO, a page for each area you cover and a link to your Google Business Profile help Google match your website to plumbing searches in your patch.",
        icon: "Pin",
      },
      {
        title: "Show the trust signals homeowners look for.",
        body: "Gas Safe registration, CIPHE membership, WaterSafe approval, before-and-after photos and your Google reviews sit where visitors look first. Only the accreditations you actually hold go on the site.",
        icon: "Check",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What should a plumber website include?",
    intro:
      "A plumber website should include a one-tap call button, a page for each service, the accreditations you hold, your reviews, the areas you cover and a quick quote form. Every plumber website from ConstructionWebDesign.uk is planned around the parts below.",
    items: [
      {
        title: "Emergency-led homepage with one-tap call.",
        body: "The first thing a visitor sees on a phone is your number, not a slideshow. A sticky call bar stays on screen as they scroll, so someone with a burst pipe or no hot water can ring you in one tap. If you take 24/7 callouts, the homepage says so up front.",
      },
      {
        title: "A page for every plumbing service.",
        body: "Boiler repairs, central heating, drainage, bathroom installs, leak detection, gas safety checks and same-day callouts each get their own page. Each page targets its own search, so a homeowner looking for a boiler repair lands on your boiler repair page, not a general list.",
      },
      {
        title: "Example prices and a quick quote form.",
        body: "Publish typical prices for common jobs if you want to. The quote form asks for a postcode, a photo and a short description of the problem, so you have what you need to price the job before you ring back.",
      },
      {
        title: "Gas Safe, CIPHE and WaterSafe badges near the top.",
        body: "Gas Safe registration is the legal register of gas engineers in the UK, and homeowners can check a number on it. If you're Gas Safe registered, a CIPHE (Chartered Institute of Plumbing and Heating Engineering) member or WaterSafe approved, the badge sits in the header with your registration number linked to the official register, not buried in the footer. Anything you don't hold stays off the site.",
      },
      {
        title: "Reviews and before-and-after photos.",
        body: "Your Google reviews and before-and-after photos of real jobs, such as bathroom refits, boiler swaps and drain repairs, sit on the pages where homeowners decide who to call.",
      },
      {
        title: "Local SEO built in from day one.",
        body: "Every plumber website includes on-page local SEO: schema markup, location pages for the towns you cover, postcode and town keywords in page titles and a link to your Google Business Profile.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a plumber website cost in the UK?",
      a: "A plumber website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with a logo and branding. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer billing £50–£120 an hour, £5,000–£25,000 for an agency, or about £22 a month for Wix or Squarespace. You own the website.",
    },
    {
      q: "How long does it take to build a plumber website?",
      a: "Plumber websites from ConstructionWebDesign.uk go live 7 days after the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. On the discovery call you explain the jobs you want more of and the areas you cover. For comparison, a typical UK agency project takes 8–12 weeks.",
    },
    {
      q: "What pages should a plumber's website have?",
      a: "A plumber's website should have a homepage with a one-tap call button, a page for each service (boilers, central heating, drainage, leaks, emergency callouts), pages for the areas you cover, your reviews and a contact page with a short quote form. Gas Safe, CIPHE or WaterSafe badges belong near the top if you hold them. ConstructionWebDesign.uk agrees the sitemap with you before design starts.",
    },
    {
      q: "Do plumbers need a website?",
      a: "Yes, plumbers need a website, even as a one-person business. A website gives a homeowner who finds you on Google, Checkatrade or the side of your van somewhere to check your services, reviews and accreditations before they ring. Directory profiles list you next to other plumbers. Your own website shows only your business, takes enquiries while you're on a job and belongs to you.",
    },
    {
      q: "How do plumbers get found on Google?",
      a: "Plumbers get found on Google through a website with a page for each service and area, on-page local SEO and a complete Google Business Profile with regular reviews. Every plumber website from ConstructionWebDesign.uk includes on-page local SEO: schema markup, location pages, postcode and town keywords and a link to your Google Business Profile. Nobody can honestly guarantee a ranking, but these are the basics a plumber needs in place.",
    },
    {
      q: "Should a plumber show their Gas Safe number on their website?",
      a: "Yes, a plumber who works on gas appliances should show their Gas Safe registration number on the website. Gas Safe registration is the legal register of gas engineers in the UK, and homeowners can check a number on the Gas Safe Register before booking. ConstructionWebDesign.uk puts the badge and number near the top of the page, linked to the official register. Plumbers who don't do gas work leave it off.",
    },
    {
      q: "Will a plumber website work on mobile for emergency callouts?",
      a: "Yes, every plumber website from ConstructionWebDesign.uk is built mobile-first for emergency callouts. Your phone number and a one-tap call button are the first thing on a phone screen, and a sticky call bar stays visible as the visitor scrolls. Someone with a burst pipe or a leaking boiler can ring you straight away without hunting for a contact page.",
    },
    {
      q: "Can I build my own plumber website on Wix or Squarespace?",
      a: "Yes, a plumber can build a website on Wix, Squarespace or WordPress, and Wix or Squarespace typically cost about £22 a month. The trade-off is time: you write the service pages, set up local SEO and add your Gas Safe, CIPHE or WaterSafe details yourself, and a Wix or Squarespace site goes offline if you stop paying. ConstructionWebDesign.uk builds websites for plumbers at a fixed £1,000, and you own the result.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/google-business-profile-for-builders",
      label: "Google Business Profile for builders: setup guide",
    },
    {
      href: "/blog/local-seo-for-builders",
      label: "Local SEO for builders: rank for \"builder near me\"",
    },
  ],
  finalCta: {
    headline: "Talk to us about your plumber website.",
    highlight: "plumber website",
    sub: "Book a free 30-minute call or email hello@constructionwebdesign.uk. Fixed £1,000, one-off, and your website is live in 7 days.",
  },
};
